import type {
  OverviewShipment,
  RouteDetail,
  RouteParcelInfo,
  RouteShipmentInfo,
  RouteSummary,
} from '../domain/routes';
import { isPartiallyScanned, isUnfinished, resolveProgress, sortForMaster } from '../domain/routes';
import type {
  OverviewResponseDto,
  RouteDetailResponseDto,
  RouteListResponseDto,
  RouteShipmentDto,
} from './types';

/**
 * Hranice mezi API a doménou pro přehled tras.
 *
 * Počty se dopočítávají z pole `parcels`, ne z počítadel `preloadedParcels` /
 * `whScannedParcels`. Je to stejné pravidlo jako u `order.packages` (kontrakt
 * 8.4): jeden zdroj pravdy nemůže sám se sebou nesouhlasit. Počítadla ze
 * serveru se použijí jen tam, kde rozpad na balíky nedorazil.
 */
export function mapRouteList(dto: RouteListResponseDto): RouteSummary[] {
  return dto.routes.map((r) => {
    // `whScannedParcels` počítá jen kusy se skenem, naložené do něj nespadají.
    // Sjednocení „viděné“ je proto `max` z obou čísel — přesný průnik ze dvou
    // souhrnů spočítat nejde a `max` je bezpečná dolní mez (viz `scannedParcels`).
    const scannedParcels = Math.max(r.whScannedParcels ?? 0, r.loadedParcels);

    return {
      routeNo: r.routeNo,
      driverName: r.driverName ?? null,
      vehicle: r.vehicle ?? null,
      plate: r.plate ?? null,
      shipments: r.shipments,
      parcels: r.parcels,
      loadedParcels: r.loadedParcels,
      scannedParcels,
      remainingParcels: Math.max(0, r.parcels - r.loadedParcels),
      unseenParcels: Math.max(0, r.parcels - scannedParcels),
      mine: r.mine,
      complete: r.parcels > 0 && r.loadedParcels >= r.parcels,
    };
  });
}

export function mapRouteDetail(dto: RouteDetailResponseDto): RouteDetail {
  const shipments = sortForMaster(dto.shipments.map(mapShipment));

  return {
    routeNo: dto.routeNo,
    driverName: dto.driverName ?? null,
    vehicle: dto.vehicle ?? null,
    plate: dto.plate ?? null,
    shipments,
    totals: {
      shipments: shipments.length,
      parcels: sum(shipments, (s) => s.parcelCount),
      scanned: sum(shipments, (s) => s.scannedCount),
      loaded: sum(shipments, (s) => s.loadedCount),
      unfinished: shipments.filter(isUnfinished).length,
    },
  };
}

/**
 * Přehled rozskenovaných zásilek napříč trasami.
 *
 * Server posílá zásilky, které považuje za nedokončené; aplikace si z
 * `parcels[]` dopočítá, které jsou opravdu **rozskenované** (část kusů
 * naskenovaná, část ne). Filtrovat až tady je schválně — server nemusí znát
 * definici z `domain/routes.ts` a nemůže se s aplikací rozejít.
 *
 * Řazení: nejrozdělanější nahoru (nejvíc chybějících kusů), pak podle čísla
 * zásilky, ať je pořadí stabilní mezi načteními.
 */
export function mapOverview(dto: OverviewResponseDto): OverviewShipment[] {
  return dto.shipments
    .map((s) => ({ ...mapShipment(s), routeNo: s.routeNo ?? null }))
    .filter(isPartiallyScanned)
    .sort(
      (a, b) =>
        b.parcelCount - b.scannedCount - (a.parcelCount - a.scannedCount) ||
        a.shipmentNumber.localeCompare(b.shipmentNumber),
    );
}

function mapShipment(dto: RouteShipmentDto): RouteShipmentInfo {
  const parcels: RouteParcelInfo[] = (dto.parcels ?? []).map((p) => ({
    packageNumber: p.packageNumber,
    temperature: p.temp ?? null,
    loadedAt: p.loadedAt ?? null,
    whScannedAt: p.whScannedAt ?? null,
  }));

  const hasParcels = parcels.length > 0;

  return {
    shipmentId: dto.shipmentId,
    shipmentNumber: dto.shipmentNumber,
    sequence: dto.seq ?? null,
    recipientName: dto.name ?? null,
    address: dto.addr ?? null,
    sender: dto.sender ?? null,
    weightKg: dto.kg ?? null,

    parcels,
    parcelCount: parcels.length,
    // Bez rozpadu na balíky zbývají jen dvě souhrnná čísla, a `whScannedParcels`
    // naložené kusy nezapočítává — proto `max`, ne součet: kus může být v obou.
    scannedCount: hasParcels
      ? parcels.filter((p) => p.whScannedAt !== null || p.loadedAt !== null).length
      : Math.max(dto.whScannedParcels, dto.preloadedParcels),
    loadedCount: hasParcels ? parcels.filter((p) => p.loadedAt !== null).length : dto.preloadedParcels,

    progress: resolveProgress(parcels),
  };
}

function sum(items: RouteShipmentInfo[], pick: (s: RouteShipmentInfo) => number): number {
  return items.reduce((acc, item) => acc + pick(item), 0);
}
